import Phaser from 'phaser';

/**
 * Asset types supported by the loader
 */
export type AssetType =
  | 'image'
  | 'spritesheet'
  | 'audio'
  | 'atlas'
  | 'tilemapJson'
  | 'bitmapFont';

/**
 * Asset definition
 */
export interface AssetDefinition {
  key: string;
  type: AssetType;
  url: string;
  // For spritesheets
  frameConfig?: Phaser.Types.Loader.FileTypes.ImageFrameConfig;
  // For atlas
  atlasUrl?: string;
  // For audio
  audioConfig?: Phaser.Types.Loader.FileTypes.AudioFileConfig;
}

/**
 * Asset manifest for a game
 */
export interface AssetManifest {
  gameId: string;
  basePath?: string;
  assets: AssetDefinition[];
}

/**
 * Loading progress callback
 */
export type ProgressCallback = (progress: number, file?: string) => void;

/**
 * Asset Loader
 * Manages loading game assets with progress tracking.
 */
export class AssetLoader {
  private scene: Phaser.Scene;
  private basePath: string;

  constructor(scene: Phaser.Scene, basePath = '/assets/games/') {
    this.scene = scene;
    this.basePath = basePath;
  }

  /**
   * Load assets from a manifest
   */
  loadManifest(
    manifest: AssetManifest,
    onProgress?: ProgressCallback
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const path = manifest.basePath || `${this.basePath}${manifest.gameId}/`;

      // Set up progress tracking
      if (onProgress) {
        this.scene.load.on('progress', (value: number) => {
          onProgress(value);
        });

        this.scene.load.on('fileprogress', (file: Phaser.Loader.File) => {
          onProgress(this.scene.load.progress, file.key);
        });
      }

      // Queue all assets
      for (const asset of manifest.assets) {
        this.queueAsset(asset, path);
      }

      // Handle completion
      this.scene.load.once('complete', () => {
        this.scene.load.off('progress');
        this.scene.load.off('fileprogress');
        resolve();
      });

      // Handle errors
      this.scene.load.once('loaderror', (file: Phaser.Loader.File) => {
        reject(new Error(`Failed to load asset: ${file.key}`));
      });

      // Start loading
      this.scene.load.start();
    });
  }

  /**
   * Queue a single asset for loading
   */
  private queueAsset(asset: AssetDefinition, basePath: string): void {
    const url = asset.url.startsWith('http')
      ? asset.url
      : `${basePath}${asset.url}`;

    switch (asset.type) {
      case 'image':
        this.scene.load.image(asset.key, url);
        break;

      case 'spritesheet':
        if (asset.frameConfig) {
          this.scene.load.spritesheet(asset.key, url, asset.frameConfig);
        } else {
          throw new Error(`Spritesheet ${asset.key} requires frameConfig`);
        }
        break;

      case 'audio':
        this.scene.load.audio(asset.key, url, asset.audioConfig);
        break;

      case 'atlas':
        if (asset.atlasUrl) {
          const atlasPath = asset.atlasUrl.startsWith('http')
            ? asset.atlasUrl
            : `${basePath}${asset.atlasUrl}`;
          this.scene.load.atlas(asset.key, url, atlasPath);
        } else {
          throw new Error(`Atlas ${asset.key} requires atlasUrl`);
        }
        break;

      case 'tilemapJson':
        this.scene.load.tilemapTiledJSON(asset.key, url);
        break;

      case 'bitmapFont':
        if (asset.atlasUrl) {
          const fontPath = asset.atlasUrl.startsWith('http')
            ? asset.atlasUrl
            : `${basePath}${asset.atlasUrl}`;
          this.scene.load.bitmapFont(asset.key, url, fontPath);
        } else {
          throw new Error(
            `BitmapFont ${asset.key} requires atlasUrl (font data)`
          );
        }
        break;

      default:
        throw new Error(`Unknown asset type: ${asset.type}`);
    }
  }

  /**
   * Load a single image
   */
  loadImage(key: string, url: string): Promise<void> {
    return this.loadManifest({
      gameId: '',
      basePath: '',
      assets: [{ key, type: 'image', url }],
    });
  }

  /**
   * Load multiple images
   */
  loadImages(images: Array<{ key: string; url: string }>): Promise<void> {
    return this.loadManifest({
      gameId: '',
      basePath: '',
      assets: images.map((img) => ({ ...img, type: 'image' as const })),
    });
  }

  /**
   * Load a spritesheet
   */
  loadSpritesheet(
    key: string,
    url: string,
    frameWidth: number,
    frameHeight: number,
    frameCount?: number
  ): Promise<void> {
    return this.loadManifest({
      gameId: '',
      basePath: '',
      assets: [
        {
          key,
          type: 'spritesheet',
          url,
          frameConfig: {
            frameWidth,
            frameHeight,
            endFrame: frameCount ? frameCount - 1 : undefined,
          },
        },
      ],
    });
  }

  /**
   * Load audio
   */
  loadAudio(key: string, url: string | string[]): Promise<void> {
    const urls = Array.isArray(url) ? url : [url];
    return this.loadManifest({
      gameId: '',
      basePath: '',
      assets: [{ key, type: 'audio', url: urls[0] }],
    });
  }

  /**
   * Check if an asset is loaded
   */
  isLoaded(key: string): boolean {
    return (
      this.scene.textures.exists(key) ||
      this.scene.cache.audio.exists(key) ||
      this.scene.cache.tilemap.exists(key)
    );
  }

  /**
   * Get loading progress (0-1)
   */
  getProgress(): number {
    return this.scene.load.progress;
  }
}

/**
 * Create a simple procedural texture
 * Useful for prototyping before real assets are ready
 */
export function createProceduralTexture(
  scene: Phaser.Scene,
  key: string,
  width: number,
  height: number,
  color: number,
  borderColor?: number,
  borderWidth = 2
): void {
  const graphics = scene.make.graphics({ x: 0, y: 0 });

  // Fill
  graphics.fillStyle(color);
  graphics.fillRect(0, 0, width, height);

  // Border
  if (borderColor !== undefined) {
    graphics.lineStyle(borderWidth, borderColor);
    graphics.strokeRect(
      borderWidth / 2,
      borderWidth / 2,
      width - borderWidth,
      height - borderWidth
    );
  }

  // Generate texture
  graphics.generateTexture(key, width, height);
  graphics.destroy();
}

/**
 * Create a circular procedural texture
 */
export function createCircleTexture(
  scene: Phaser.Scene,
  key: string,
  radius: number,
  color: number,
  borderColor?: number,
  borderWidth = 2
): void {
  const size = radius * 2;
  const graphics = scene.make.graphics({ x: 0, y: 0 });

  // Fill
  graphics.fillStyle(color);
  graphics.fillCircle(radius, radius, radius);

  // Border
  if (borderColor !== undefined) {
    graphics.lineStyle(borderWidth, borderColor);
    graphics.strokeCircle(radius, radius, radius - borderWidth / 2);
  }

  // Generate texture
  graphics.generateTexture(key, size, size);
  graphics.destroy();
}
